import { fetchPrices } from '@client/api/products'
import { t } from '@client/i18n/localize'

// Constants
const SELECTORS = {
  PRODUCT_ITEM: '.twn-product-tile-dynamic[data-item-id]',
  PRICE_AMOUNT: '.twn-price-amount span',
  PRICE_TERM: '.twn-price-term span'
}

// Pattern to extract product ID from data-item-id attribute (e.g., "101376-6506" -> 6506)
const DATA_ITEM_ID_PATTERN = /^\d+-(\d+)$/
const LOG_PREFIX = '[Tweakwise Price Updater]'

/**
 * TweakwisePriceUpdater
 *
 * Automatically updates product prices in Tweakwise search results
 * when users change rental dates via the cart picker.
 *
 * @example
 *   // Initialize (called automatically on page load)
 *   tweakwisePriceUpdater.init()
 *
 *   // Manually trigger update
 *   tweakwisePriceUpdater.updatePrices()
 *
 *   // Cleanup on page unload
 *   tweakwisePriceUpdater.destroy()
 */
class TweakwisePriceUpdater {
  constructor(options = {}) {
    this.debug = options.debug || false
    this.previousPickupTime = null
    this.previousReturnTime = null
    this.unsubscribe = null
    this.isUpdating = false
  }


  /**
   * Initialize the price updater and subscribe to Redux store changes
   */
  init() {
    if (!window.reduxStore) {
      console.warn(`${LOG_PREFIX} Redux store not available`)
      return
    }

    // Prevent multiple subscriptions
    if (this.unsubscribe) {
      return
    }

    this.unsubscribe = window.reduxStore.subscribe(() => this.handleStoreChange())
    this.syncCartState()

    // Trigger initial price update if dates are already set
    this.updatePricesIfDatesSet()
  }

  /**
   * Clean up subscriptions
   */
  destroy() {
    if (this.unsubscribe) {
      this.unsubscribe()
      this.unsubscribe = null
    }
  }

  // Redux store handling

  /**
   * Sync initial cart state from Redux store
   */
  syncCartState() {
    const state = window.reduxStore.getState()
    this.previousPickupTime = state.cart?.pickup_time
    this.previousReturnTime = state.cart?.return_time
  }

  /**
   * Update prices if cart dates are already set
   * Checks current Redux state, not cached state
   */
  updatePricesIfDatesSet() {
    if (!window.reduxStore) return

    const state = window.reduxStore.getState()
    const pickupTime = state.cart?.pickup_time
    const returnTime = state.cart?.return_time

    if (this.areDatesValid(pickupTime, returnTime)) {
      this.updatePrices()
    } else {
      this.updateDefaultPriceTerms()
    }
  }

  /**
   * Update the default price term text rendered by Tweakwise template
   * with the correct translated text when no dates are selected
   */
  updateDefaultPriceTerms() {
    const priceTermElements = document.querySelectorAll(SELECTORS.PRICE_TERM)
    const translatedTerm = t('per_day_suffix')

    priceTermElements.forEach(element => {
      element.textContent = translatedTerm
      element.parentElement?.setAttribute('data-property-value', translatedTerm)
    })
  }

  /**
   * Handle Redux store changes and trigger price updates
   */
  handleStoreChange() {
    const state = window.reduxStore.getState()
    const currentPickupTime = state.cart?.pickup_time
    const currentReturnTime = state.cart?.return_time

    if (this.hasCartDatesChanged(currentPickupTime, currentReturnTime)) {
      this.previousPickupTime = currentPickupTime
      this.previousReturnTime = currentReturnTime

      if (this.areDatesValid(currentPickupTime, currentReturnTime)) {
        this.updatePrices()
      }
    }
  }

  /**
   * Check if cart dates have changed
   */
  hasCartDatesChanged(currentPickupTime, currentReturnTime) {
    return (
      currentPickupTime !== this.previousPickupTime ||
      currentReturnTime !== this.previousReturnTime
    )
  }

  /**
   * Check if both pickup and return dates are set
   */
  areDatesValid(pickupTime, returnTime) {
    return pickupTime && returnTime
  }

  // Product ID extraction

  /**
   * Extract product IDs from Tweakwise DOM elements
   * data-item-id format: "101376-6506" -> 6506
   */
  getProductIdsFromDOM() {
    const productElements = document.querySelectorAll(SELECTORS.PRODUCT_ITEM)
    const productIds = new Set()

    productElements.forEach(element => {
      const productId = this.extractProductIdFromElement(element)
      if (productId) {
        productIds.add(productId)
      }
    })

    return Array.from(productIds)
  }

  /**
   * Extract product ID from data-item-id attribute
   * Format: "101376-6506" -> 6506
   */
  extractProductIdFromElement(element) {
    const dataItemId = element.getAttribute('data-item-id')
    if (!dataItemId) return null

    const match = dataItemId.match(DATA_ITEM_ID_PATTERN)
    return match ? parseInt(match[1], 10) : null
  }

  // Price updates

  /**
   * Update prices for all visible products
   */
  async updatePrices() {
    if (this.isUpdating) return

    this.isUpdating = true

    try {
      const productIds = this.getProductIdsFromDOM()

      if (productIds.length === 0) return

      const pricesData = await this.fetchPricesData(productIds)
      this.updateProductPrices(pricesData)
    } catch (error) {
      console.error(`${LOG_PREFIX} Failed to update prices:`, error)
    } finally {
      this.isUpdating = false
    }
  }

  /**
   * Fetch prices from API
   */
  async fetchPricesData(productIds) {
    const response = await fetchPrices({ ids: productIds })
    return response.data.prices
  }

  /**
   * Update all product prices in the DOM
   */
  updateProductPrices(pricesData) {
    Object.entries(pricesData).forEach(([productId, priceData]) => {
      this.updateProductPrice(productId, priceData)
    })
  }

  /**
   * Update a single product's price in the DOM
   */
  updateProductPrice(productId, priceData) {
    if (!priceData || priceData.length === 0) return

    const productElements = document.querySelectorAll(
      `.twn-product-tile-dynamic[data-item-id$="-${productId}"]`
    )

    productElements.forEach(element => {
      this.updatePriceElements(element, priceData[0])
    })
  }

  /**
   * Update price amount and term elements
   */
  updatePriceElements(element, price) {
    const priceAmountElement = element.querySelector(SELECTORS.PRICE_AMOUNT)
    const priceTermElement = element.querySelector(SELECTORS.PRICE_TERM)

    if (!priceAmountElement) return

    this.updatePriceAmount(priceAmountElement, price)
    this.updatePriceTerm(priceTermElement, price)
  }

  /**
   * Update price amount element and its parent data attribute
   */
  updatePriceAmount(element, price) {
    const formattedAmount = price.formattedPrice ||
      `${price.integerPart}${price.decimalPart}`

    element.textContent = formattedAmount
    element.parentElement?.setAttribute('data-property-value', formattedAmount)
  }

  /**
   * Update price term element and its parent data attribute
   */
  updatePriceTerm(element, price) {
    if (!element || !price.title) return

    const term = t('per_day_on_rental', { period: price.title })
    element.textContent = term
    element.parentElement?.setAttribute('data-property-value', term)
  }
}

// Export singleton instance
export default new TweakwisePriceUpdater()
